import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { JiraAssetView } from "@msc/shared";

import { api } from "../lib/api.js";

/**
 * Verknüpfung Beauftragung ↔ Jira-Kundenabteilung (F1/F5).
 *
 * Bewusst eigene Query je Beauftragung statt Teil der Kunden-360-Antwort: Die
 * Auswahlliste zieht alle Kundenabteilungen des Spiegels. Das an jede
 * Kundenansicht zu hängen hieße, für 300 Kunden Daten zu holen, die fast nie
 * jemand ansieht. Was die Kundenakte dauerhaft braucht — ob eine Beauftragung
 * verknüpft ist und mit welcher Abteilung —, steht dagegen in der 360-Antwort.
 */

const key = (engagementId: string, query: string) => [
  "jira-asset",
  engagementId,
  query,
];

export function useJiraAsset(
  engagementId: string,
  query: string,
  enabled: boolean,
) {
  return useQuery<JiraAssetView>({
    queryKey: key(engagementId, query),
    queryFn: () =>
      api.get<JiraAssetView>(
        `/engagements/${engagementId}/jira-asset?q=${encodeURIComponent(query)}`,
      ),
    enabled,
  });
}

/** Setzt die Verknüpfung — `null` löst sie. */
export function useSetJiraAsset(engagementId: string, customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kundenabteilungId: string | null) =>
      api.put<{ ok: true }>(`/engagements/${engagementId}/jira-asset`, {
        kundenabteilungId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["jira-asset", engagementId] });
      // Die Kundenakte zeigt Name und Lead je nach Verknüpfung bearbeitbar
      // oder nicht — ohne diese Invalidierung bliebe sie auf dem alten Stand.
      void qc.invalidateQueries({ queryKey: ["customers", customerId] });
      // Der Statusbericht zieht seine Ticketblöcke aus genau dieser
      // Verknüpfung — sonst zeigte er nach dem Verknüpfen noch die alte Sicht.
      void qc.invalidateQueries({ queryKey: ["status-reports"] });
    },
  });
}
